<script lang="ts">
    import AlertIcon from "../../../svelte/icons/alertIcon.svelte";
    import CheckIcon from "../../../svelte/icons/checkIcon.svelte";
    import { formatDateLocale } from "@/lib/dateUtils";
    import type { PageStateAny } from "../state";
    import type { Job, SetView, View, VehicleAssignmentMap, SchedularEmployeeAssignment } from '../types';
    import type { ValidationStoreInner } from "../validate";
    import { SvelteMap } from 'svelte/reactivity';

    type Props = {
        scrollToDate: SetView;
        view: View;
        vehicleAssignmentMap: VehicleAssignmentMap;
        isDisplaying: boolean;
        modifiedCount: number;
        pageState: PageStateAny;
        validationStore: ValidationStoreInner;
        overlappingEmployeeAssignments: SvelteMap<string, {employee_id: string, assignment1: SchedularEmployeeAssignment, assignment2: SchedularEmployeeAssignment}>;
    };
    let {
        scrollToDate,
        view,
        vehicleAssignmentMap,
        isDisplaying = $bindable(),
        modifiedCount,
        pageState,
        validationStore,
        overlappingEmployeeAssignments ,
    }: Props = $props();


    const highlightJob = (job: Job) => {
        scrollToDate({
            displayDate: job.start ?? view.displayDate,
            scrollLeft: 250,
        });
        isDisplaying = false;
        pageState.selected = {
            type: "job",
            item: job,
            dragging: null,
        };
    };
</script>

<div class="space-y-4 rounded-lg">
    {#if validationStore.missingJobType.length > 0}
        <div class="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20">
            <AlertIcon />
            <div class="flex-1">
                <p class="font-medium text-red-700 dark:text-red-400">{validationStore.missingJobType.length} {validationStore.missingJobType.length === 1 ? 'job is' : 'jobs are'} missing Job Type</p>
                <p class="text-sm text-red-600 dark:text-red-500 mb-2">Please select a job type for the following:</p>
                <ul class="text-sm space-y-1">
                    {#each validationStore.missingJobType as job (job.id)}
                        <li>
                            <button
                                type="button"
                                class="text-red-700 dark:text-red-400 hover:underline cursor-pointer"
                                onclick={() => highlightJob(job)}
                            >
                                {job.site_name || job.title || 'Unnamed job'}
                            </button>
                        </li>
                    {/each}
                </ul>
            </div>
        </div>
    {/if}

    {#if validationStore.duplicateExclusiveType.length > 0}
        <div class="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20">
            <AlertIcon />
            <div class="flex-1">
                <p class="font-medium text-red-700 dark:text-red-400">Duplicate Erect/Strike assignments</p>
                <p class="text-sm text-red-600 dark:text-red-500 mb-2">Each job can only have one Erect and one Strike assignment:</p>
                <ul class="text-sm space-y-1">
                    {#each validationStore.duplicateExclusiveType as job (job.id)}
                        <li>
                            <button
                                type="button"
                                class="text-red-700 dark:text-red-400 hover:underline cursor-pointer"
                                onclick={() => highlightJob(job)}
                            >
                                {job.site_name || job.title || 'Unnamed job'} ({job.job_type})
                            </button>
                        </li>
                    {/each}
                </ul>
            </div>
        </div>
    {/if}
    {#if validationStore.missingPurpose.length > 0}
        <div class="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20">
            <AlertIcon />
            <div class="flex-1">
                <p class="font-medium text-red-700 dark:text-red-400">{validationStore.missingPurpose.length} {validationStore.missingPurpose.length === 1 ? 'site visit is' : 'site visits are'} missing Purpose</p>
                <p class="text-sm text-red-600 dark:text-red-500 mb-2">Please select a purpose for the following:</p>
                <ul class="text-sm space-y-1">
                    {#each validationStore.missingPurpose as job (job.id)}
                        <li>
                            <button
                                type="button"
                                class="text-red-700 dark:text-red-400 hover:underline cursor-pointer"
                                onclick={() => highlightJob(job)}
                            >
                                {job.title || job.site_name || 'Unnamed site visit'}
                            </button>
                        </li>
                    {/each}
                </ul>
            </div>
        </div>
    {/if}
    <div class="flex items-start gap-3 p-3 rounded-lg {overlappingEmployeeAssignments.size === 0 ? 'bg-green-50 dark:bg-green-950/20' : 'bg-amber-50 dark:bg-amber-950/20'}">
        {#if overlappingEmployeeAssignments.size > 0}
            <AlertIcon />
            <div class="flex-1">
                <p class="font-medium text-amber-700 dark:text-amber-400">{overlappingEmployeeAssignments.size} {overlappingEmployeeAssignments.size === 1 ? 'employee has' : 'employees have'} overlapping assignments</p>
                <p class="text-sm text-amber-600 dark:text-amber-500">These employees are assigned to multiple jobs at the same time.</p>
            </div>
        {:else}
            <CheckIcon />
            <div class="flex-1">
                <p class="font-medium text-green-700 dark:text-green-400">No scheduling conflicts</p>
                <p class="text-sm text-green-600 dark:text-green-500">All employee assignments are properly scheduled without overlaps.</p>
            </div>
        {/if}
    </div>

    <div class="pt-4 border-t">
        {#if modifiedCount > 0}
            <p class="text-base font-medium">You have modified <span class="text-primary font-semibold">{modifiedCount}</span> {modifiedCount === 1 ? 'job' : 'jobs'}.</p>
            {#if validationStore.missingJobType.length > 0 || validationStore.missingPurpose.length > 0 || validationStore.duplicateExclusiveType.length > 0}
                <p class="text-sm text-red-600 dark:text-red-500 mt-1">Please fix the validation errors above before saving.</p>
            {:else}
                <p class="text-sm text-muted-foreground mt-1">Click "Save Changes" below to persist your modifications.</p>
            {/if}
        {:else}
            <p class="text-base text-muted-foreground">No changes have been made to the schedule.</p>
        {/if}
    </div>
</div>

<style lang="scss">
    .title {
        margin: 0;
    }
    ul {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }
    li {
        margin-left: 1rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }
    .success-text {
        color: hsl(var(--clr-success-9));
    }
    .error-text {
        color: hsl(var(--clr-error-5));
    }
    .warn-text {
        color: hsl(var(--clr-warning-8));
    }
</style>
